// Snapshots — the fleet view. Which sub-accounts run a snapshot, on what version, and how far
// each has drifted from the baseline.
//
// No GHL snapshot API is involved: GHL stamps assets it creates from a snapshot with an
// `originId` that is stable across every account the snapshot was pushed to, so a baseline is
// simply the {originId → fingerprint} map of a documented master account.
import { useEffect, useMemo, useState } from 'react';
import type { SnapshotRecord, SubAccount } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { Button, Card, EmptyNote, ErrorNote, SectionHeader, Select, Spinner } from './ui';

const DRIFT_STYLE: Record<string, string> = {
  modified: 'bg-amber-100 text-amber-700',
  missing: 'bg-rose-100 text-rose-700',
  extra: 'bg-sky-100 text-sky-700',
  matched: 'bg-emerald-100 text-emerald-700',
};

function DriftBar({ d }: { d: { matched: number; modified: number; missing: number; extra: number } }) {
  const total = d.matched + d.modified + d.missing + d.extra;
  if (!total) return <span className="text-xs text-slate-400">no data</span>;
  const seg = (n: number, cls: string, label: string) =>
    n > 0 ? <div className={cls} style={{ width: `${(n / total) * 100}%` }} title={`${n} ${label}`} /> : null;
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
      {seg(d.matched, 'bg-emerald-400', 'in sync')}
      {seg(d.modified, 'bg-amber-400', 'edited downstream')}
      {seg(d.missing, 'bg-rose-400', 'deleted')}
      {seg(d.extra, 'bg-sky-400', 'added locally')}
    </div>
  );
}

export default function Snapshots() {
  const [snaps, setSnaps] = useState<SnapshotRecord[] | null>(null);
  const [accounts, setAccounts] = useState<SubAccount[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', source: '', version: '' });
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = async () => {
    try {
      const [s, a] = await Promise.all([agencyApi.snapshots(), agencyApi.accounts()]);
      setSnaps(s);
      setAccounts(a);
      setError('');
    } catch (e) { setError((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);

  // Only accounts we have documented can serve as a baseline — the fingerprints come from the scan.
  const documented = useMemo(() => accounts.filter((a) => a.last_scan_at), [accounts]);

  const create = async () => {
    if (!form.name || !form.source) return;
    setBusy('create');
    try {
      await agencyApi.createSnapshot({ name: form.name, source_location_id: form.source, version_label: form.version || undefined });
      setCreating(false);
      setForm({ name: '', source: '', version: '' });
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };

  const check = async (id: string) => {
    setBusy(id);
    try { await agencyApi.checkSnapshot(id); await load(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };

  return (
    <div>
      <SectionHeader
        title="Snapshots"
        subtitle="Track which sub-accounts run each snapshot, and how far they have drifted from it."
        action={<Button onClick={() => setCreating((v) => !v)}>{creating ? 'Cancel' : 'Capture baseline'}</Button>}
      />

      {error && <div className="mb-4"><ErrorNote error={error} /></div>}

      {creating && (
        <Card className="mb-4 p-4">
          <h3 className="mb-1 text-sm font-semibold text-slate-900">Capture a baseline</h3>
          <p className="mb-3 text-sm text-slate-500">
            Pick the documented account that IS the snapshot master. We fingerprint its
            snapshot-stamped assets; every other account is then compared against it.
          </p>
          {!documented.length ? (
            <p className="text-sm text-amber-700">
              No documented accounts yet — document a master account first (Sub-Accounts → Document).
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Snapshot name</span>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Core Client Build"
                  className="w-56 rounded-lg border border-slate-200 px-3 py-1.5 outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Master account</span>
                <Select value={form.source} onChange={(v) => setForm({ ...form, source: v })}>
                  <option value="">Choose…</option>
                  {documented.map((a) => (
                    <option key={a.location_id} value={a.location_id}>{a.client_name ?? a.location_id}</option>
                  ))}
                </Select>
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-slate-600">Version label</span>
                <input
                  value={form.version}
                  onChange={(e) => setForm({ ...form, version: e.target.value })}
                  placeholder="v1.4"
                  className="w-28 rounded-lg border border-slate-200 px-3 py-1.5 outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                />
              </label>
              <Button onClick={create} disabled={busy === 'create' || !form.name || !form.source}>
                {busy === 'create' ? 'Capturing…' : 'Capture'}
              </Button>
            </div>
          )}
        </Card>
      )}

      {!snaps ? <Spinner label="Loading snapshots…" /> : !snaps.length ? (
        <EmptyNote>
          No snapshots tracked yet. Capture a baseline from a documented master account to start
          measuring drift across the fleet.
        </EmptyNote>
      ) : (
        <div className="space-y-4">
          {snaps.map((s) => {
            const deps = s.deployments ?? [];
            const drifted = deps.filter((d) => d.modified + d.missing > 0).length;
            return (
              <Card key={s.id} className="overflow-hidden">
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 p-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-900">{s.name}</h3>
                      {s.version_label && (
                        <span className="rounded bg-slate-100 px-1.5 py-px text-xs text-slate-600">{s.version_label}</span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {s.asset_count} baseline assets · {deps.length} deployment{deps.length === 1 ? '' : 's'}
                      {drifted > 0 && <span className="text-amber-700"> · {drifted} drifted</span>}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => check(s.id)} disabled={busy === s.id}>
                      {busy === s.id ? 'Checking…' : 'Check fleet'}
                    </Button>
                  </div>
                </div>

                {!deps.length ? (
                  <p className="p-4 text-sm text-slate-500">
                    No deployments found yet. <strong>Check fleet</strong> scans your documented
                    sub-accounts for assets carrying this snapshot&apos;s origin ids.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-2 font-medium">Sub-account</th>
                        <th className="w-40 px-3 py-2 font-medium">Sync</th>
                        <th className="px-3 py-2 font-medium">In sync</th>
                        <th className="px-3 py-2 font-medium">Edited</th>
                        <th className="px-3 py-2 font-medium">Deleted</th>
                        <th className="px-3 py-2 font-medium">Added</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {deps.map((d) => {
                        const key = `${s.id}:${d.location_id}`;
                        const open = expanded === key;
                        return (
                          <>
                            <tr key={key} className="border-b border-slate-50 last:border-0">
                              <td className="px-4 py-2.5 font-medium text-slate-800">
                                {d.client_name ?? d.location_id}
                              </td>
                              <td className="px-3 py-2.5"><DriftBar d={d} /></td>
                              <td className="px-3 py-2.5 tabular-nums text-emerald-700">{d.matched}</td>
                              <td className="px-3 py-2.5 tabular-nums text-amber-700">{d.modified || '—'}</td>
                              <td className="px-3 py-2.5 tabular-nums text-rose-700">{d.missing || '—'}</td>
                              <td className="px-3 py-2.5 tabular-nums text-sky-700">{d.extra || '—'}</td>
                              <td className="px-3 py-2.5 text-right">
                                {!!d.drift?.length && (
                                  <Button variant="ghost" onClick={() => setExpanded(open ? null : key)}>
                                    {open ? 'Hide' : 'Detail'}
                                  </Button>
                                )}
                              </td>
                            </tr>
                            {open && (
                              <tr key={`${key}-d`} className="border-b border-slate-50 bg-slate-50/60">
                                <td colSpan={7} className="px-4 py-3">
                                  <div className="max-h-64 space-y-1 overflow-y-auto">
                                    {d.drift.map((row, i) => (
                                      <div key={i} className="flex items-center gap-2 text-sm">
                                        <span className={`w-20 shrink-0 rounded px-1.5 py-px text-center text-[11px] font-medium ${DRIFT_STYLE[row.state]}`}>
                                          {row.state}
                                        </span>
                                        <span className="text-slate-700">{row.name ?? row.originId}</span>
                                        <span className="text-xs text-slate-400">{row.type.replace(/_/g, ' ')}</span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
